import { io, Socket } from 'socket.io-client';
import type { SupportMessage, SupportTicketSummary } from './supportApi';

/**
 * Live feed for the support widget, BE namespace `/support`. Receive-only: every write goes
 * through `supportApi`. One socket per tab; listeners fan out from here so the widget can
 * mount and unmount without reconnecting.
 */

type MessageListener = (m: SupportMessage) => void;
type TicketListener = (t: SupportTicketSummary) => void;

class SupportWebSocketService {
  private socket: Socket | null = null;
  private readonly url: string;
  private messageListeners = new Set<MessageListener>();
  private ticketListeners = new Set<TicketListener>();

  constructor(url?: string) {
    this.url = url || process.env.REACT_APP_WEBSOCKET_URL || 'http://localhost:3000';
  }

  connect(): void {
    if (this.socket) return;
    const token = localStorage.getItem('access_token');
    if (!token) return;

    this.socket = io(`${this.url}/support`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 3000,
      reconnectionDelayMax: 30000,
    });
    this.socket.on('support:message', (m: SupportMessage) => {
      this.messageListeners.forEach((l) => l(m));
    });
    this.socket.on('support:ticket', (t: SupportTicketSummary) => {
      this.ticketListeners.forEach((l) => l(t));
    });
    this.socket.on('error', (e: { message?: string }) => {
      // An auth failure is not transient; drop the socket so the next connect() retries with
      // whatever token is in storage by then.
      if (e?.message?.toLowerCase().includes('authentication')) this.disconnect();
    });
  }

  disconnect(): void {
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
  }

  onMessage(listener: MessageListener): () => void {
    this.messageListeners.add(listener);
    return () => {
      this.messageListeners.delete(listener);
    };
  }

  onTicket(listener: TicketListener): () => void {
    this.ticketListeners.add(listener);
    return () => {
      this.ticketListeners.delete(listener);
    };
  }

  get connected(): boolean {
    return !!this.socket?.connected;
  }
}

export const supportWebSocket = new SupportWebSocketService();
export default supportWebSocket;
